import { useMemo, useRef, useState } from "react";
import { Navigate } from "react-big-calendar";
import PropTypes from "prop-types";
import TimeGrid from "react-big-calendar/lib/TimeGrid";
import { Box, Button, Card, IconButton, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const MIN_WIDTH = 1000;

// const SMALL_SCREEN_WIDTH = 5000;
// const LARGE_SCREEN_WIDTH = 5000;

const CustomWeekView = ({
  date,
  localizer,
  //   max = localizer.endOf(new Date(), "day"),
  //   min = localizer.startOf(new Date(), "day"),
  scrollToTime = localizer.startOf(new Date(), "day"),
  ...props
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollerRef = useRef(null);
  const [arrowDisable, setArrowDisable] = useState(true);

  const handleHorizantalScroll = (element, speed, distance, step) => {
    let scrollAmount = 0;
    const slideTimer = setInterval(() => {
      element.scrollLeft += step;
      scrollAmount += Math.abs(step);
      if (scrollAmount >= distance) {
        clearInterval(slideTimer);
      }
      if (element.scrollLeft === 0) {
        setArrowDisable(true);
      } else {
        setArrowDisable(false);
      }
    }, speed);
  };

  const currRange = useMemo(
    () => CustomWeekView.range(date, { localizer }),
    [date, localizer]
  );

  return (
    <Box sx={{ position: "relative" }}>
      {isSmallScreen && (
        <Card
          sx={{
            position: "absolute",
            left: 0,
            height: "100%",
            width: "40px",
            background: `rgba(0, 0, 0, 0.15)`,
            "&:hover": {
              background: `rgba(0, 0, 0, 0.3)`,
            },
            zIndex: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => {
            handleHorizantalScroll(
              scrollerRef.current,
              25,
              100,
              -(isSmallScreen ? 200 : MIN_WIDTH / 7)
            );
          }}
          // disabled={arrowDisable}
        >
          <IconButton>
            <ArrowBackIos sx={{ color: "#fff" }} />
          </IconButton>
        </Card>
      )}

      {isSmallScreen && (
        <Card
          sx={{
            position: "absolute",
            right: 0,
            height: "100%",
            width: "40px",
            background: `rgba(0, 0, 0, 0.15)`,
            "&:hover": {
              background: `rgba(0, 0, 0, 0.3)`,
            },
            zIndex: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => {
            handleHorizantalScroll(
              scrollerRef.current,
              25,
              100,
              isSmallScreen ? 200 : MIN_WIDTH / 7
            );
          }}
        >
          <IconButton>
            <ArrowForwardIos sx={{ color: "#fff" }} />
          </IconButton>
        </Card>
      )}

      <Box
        sx={{
          flex: 1,
          overflowX: "scroll",
          width: "100%",
        }}
        ref={scrollerRef}
      >
        <Box
          sx={{
            dispaly: "flex",
            width: isSmallScreen ? `${MIN_WIDTH}px` : "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box sx={{ flex: 1 }}>
              <TimeGrid
                date={date}
                localizer={localizer}
                //   max={max}
                //   min={min}
                min={dayjs().tz(DEFAULT_TIMEZONE).startOf("d").toDate()}
                max={dayjs().tz(DEFAULT_TIMEZONE).endOf("d").toDate()}
                range={currRange}
                scrollToTime={scrollToTime}
                {...props}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

CustomWeekView.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  localizer: PropTypes.object,
  max: PropTypes.instanceOf(Date),
  min: PropTypes.instanceOf(Date),
  scrollToTime: PropTypes.instanceOf(Date),
};

CustomWeekView.range = (date, { localizer }) => {
  // 1 week
  let start = dayjs(date).tz(DEFAULT_TIMEZONE).startOf("isoWeek").toDate();
  let end = dayjs(start).tz(DEFAULT_TIMEZONE).endOf("isoWeek").toDate();

  let current = start;
  const range = [];

  while (localizer.lte(current, end, "day")) {
    range.push(current);
    current = localizer.add(current, 1, "day");
  }

  return range;
};

CustomWeekView.navigate = (date, action, { localizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -7, "day");

    case Navigate.NEXT:
      return localizer.add(date, 7, "day");

    default:
      return date;
  }
};

CustomWeekView.title = (date, { localizer }) => {
  const [start, ...rest] = CustomWeekView.range(date, { localizer });
  return localizer.format({ start, end: rest.pop() }, "dayRangeHeaderFormat");
};

export default CustomWeekView;
